//Hooks
import { get } from 'svelte/store';
import { readDeviceStatus, readDeviceSettings } from 'src/stores/device-runtime/status-selectors';
//Formatters
import { format } from 'svelte-i18n';
//Helpers
import {
	get_gen_2_component_channel_structure,
	get_local_ip,
	get_mode,
	is_gen_1,
	is_gen_2,
	isSecondaryWallDisplay,
	isWallDisplay,
	is_loqed,
	isTRV,
	isBluTRV,
	real_id,
	isFanDevice,
} from './interface_helpers';
import { getSystemStyle } from 'src/helpers/statistics/ui/chart_colors_helpers';
//Services
import { gen2Request } from '../services/gen_2_http_reqs';
import { localReq, postReq } from '../services/http';
//Stores
import { defaultStateType, getDeviceState, rebootRequired } from 'src/stores/device-runtime/states';
import { user_data } from 'src/stores/account/user';
import { rebooting_devices } from 'src/stores/device-runtime/process';
import { devices_settings, devices_status } from 'src/stores/device-runtime/status';
import { devices } from 'src/stores/lists/devices';
//Constants
import { APPLIANCE_TYPES_VALUES, HALF_SLEEPING_TYPES, SLEEPING_TYPES } from '../constants';
//Design system components
import { SystemStyles, toast } from '@Shelly/shelly-design-system';
//Types
import type { Device } from '../types/Device';
import type { SettingRequest } from '../types/requests/settings/setting';
import { errors } from './errors';
import { capFirstChar } from './utilities';
import { devices_config } from '@devices/index';
import type { JrpcResponseData } from 'src/types/requests/request_data_socket';
import { isCloneDeviceSettingsEnabled } from './settings/settings_config_helpers';
import { PREMIUM_SETTING_TITLES } from './settings/flatten_settings_rail';
import { shouldShowFactoryResetFromInputSetting } from './factory_reset_from_input_helpers';
import { isDevServerUser, isT1User } from './user_server_conditions';
import { settings_list } from 'src/components/DevicesSettings/SettingsConfigurator';
import { isVendorSpecificFW, verifySettingUnsupportedFWVersion } from './firmware_helpers';
import { hasOperationalCountersInStatus } from './operational_counters_helpers';
import { hasComponentConfigKeyInSettings } from './component_config_settings_helpers';
import { isLocalNetworkMessagingExcludedDeviceType } from './local_network_messaging_helpers';
import { resolveVentoraSensorKindFromComponent } from './sensor_config_helpers';
import type { SystemColorsTypes } from '@Shelly/shelly-design-system/helpers/types';

export const HTTPS_CERTIFICATE_DOCS_URL =
	'https://shelly-api-docs.shelly.cloud/gen2/General/CustomHTTPSCertificates';

/**
 * Success toast after factory reset. Sleeping devices are removed from the
 * account immediately, but the reset itself is queued until wake (FE-2400).
 */
export function getFactoryResetSuccessToastTitle(device: Device): string {
	const $format = get(format);
	if (SLEEPING_TYPES.includes(device?.type)) {
		return $format('factory_reset_sleeping_scc_toast', {
			default:
				'Device removed from your account. Factory reset will complete when the device wakes up.',
		});
	}
	return $format('factory_reset_scc_toast', {
		default: 'Reseting {device_name} to factory settings.',
		values: { device_name: device.name },
	});
}

const COMPONENTS_WITH_LAYOUT_V2 = new Set([
	'auto',
	'auto_gen_2',
	'auto_gen_2_emeter',
	'auto_diffuser_gen_2',
	'schedule_gen_2',
	'schedule_settings',
	'actions',
	'night_mode',
	'night_mode_rgbw',
	'night_mode_g2',
	'virtual_components',
	'webhooks_gen_2',
	'scripts_gen_2',
]);

function buildSettingProps(element, device, isSidepanel) {
	const base = { device, useNewLayout: isSidepanel };
	if (element.title === 'device_information') {
		return { ...base, showEmbeddedTransferDelete: !isSidepanel };
	}
	const sensorKind = resolveVentoraSensorKindFromComponent(element.component);
	if (sensorKind) {
		return { ...base, sensorKind };
	}
	// Some devices (e.g. US Wall Switch Gen3) expose per-input settings as
	// separate rows. When the config declares an explicit `input_id`, forward
	// it so the component targets that input instead of `device.channel`.
	if (typeof element.input_id === 'number') {
		return { ...base, inputId: element.input_id };
	}
	return base;
}

export function onSettingsResponse(
	res: SettingRequest | SettingRequest[],
	device: Device = null,
	titles: { success?: string; danger?: string } = {},
	toasts = true,
	toastMsg: string = '',
): SettingRequest {
	const $format = get(format);

	if (Array.isArray(res)) {
		res = {
			error: res.every((a) => a?.error),
			isok: res.every((a) => a?.isok),
			restart_required: res.some((a) => a?.restart_required || a?.data?.restart_required),
		};
	}
	if (res?.data?.sleeping) {
		const status = devices_status.getByRealId(device.id);
		status._sleeping = true;
		devices_status.update_device_status({ id: device.id, msg: status });
	}
	const successCheck = res?.hasOwnProperty('isok') ? res?.isok : !res?.error;
	if (successCheck) {
		if (toasts) {
			toast.open({
				type: 'success',
				title:
					(toastMsg !== ''
						? toastMsg
						: titles?.success ||
							$format('setting_scc_saved_toast', {
								default: 'Setting saved successfully.',
							})) +
					(device &&
					(SLEEPING_TYPES.includes(device.type) || HALF_SLEEPING_TYPES.includes(device.type))
						? ' ' +
							$format('info_apply_setting_on_wake_up', {
								default:
									'The device is currently sleeping. It will update once it wakes up.',
							})
						: ''),
			});
		}
		if (
			device &&
			(res?.restart_required || res?.data?.restart_required || res?.result?.restart_required)
		) {
			// rebootDevice(device, toasts); Removing auto device reboot. This will trigger RebootPrompt after setting rebootRequired store
			rebootRequired.set(device);

			return res;
		}
		if (device && res?.data?.restarting_after) {
			toast.open({
				type: 'info',
				title: $format('reboot_scc_toast', {
					default: 'Your device will reboot now.',
				}),
				duration: 5000,
			});
			rebooting_devices.update(device.id);
		}
		return res;
	}
	if (toasts) {
		if (device && res?.errors?.err_unknown_cmd) {
			toast.open({ type: 'danger', title: res.errors.err_unknown_cmd });
			return res;
		} else if (device && res?.errors) {
			if (is_gen_1(device)) {
				const code = Object.keys(res?.errors)[0];
				const text = res?.errors[code];
				toast.open({ type: 'danger', title: code, text });
			} else {
				const code = res?.errors?.code?.toString();
				//because sometimes it is a number sometimes it is a string. I think it is safer to transform code to string always to prevent NaN if we choose to transform it as number
				const text = errors(
					code,
					res?.errors?.message ||
						res?.errors?.err_perm ||
						$format('setting_err_toast', {
							default: 'Failed to apply settings.',
						}),
				);
				toast.open({ type: 'danger', title: code, text });
			}
			return res;
		}

		toast.open({
			type: 'danger',
			title:
				titles?.danger ||
				// res?.errors?.message ||
				$format('setting_err_toast', {
					default: 'Failed to apply settings.',
				}),
		});
		return res;
	}
}

export async function rebootDevice(device: Device, toasts = true) {
	let res: SettingRequest;
	const id = real_id(device.id);

	if (is_gen_2(device)) {
		res = await gen2Request(device, 'shelly.reboot', {});
	} else {
		const local_ip = get_local_ip(id);
		res = local_ip
			? await localReq(device, `${local_ip}/reboot`, 'post', {})
			: await postReq(`/device/reboot`, { id });
	}
	const $format = get(format);
	if (res.isok) {
		rebooting_devices.update(id);
		if (toasts) {
			toast.open({
				type: 'info',
				title: $format('reboot_scc_toast', {
					default: 'Your device will reboot now.',
				}),
				duration: 5000,
			});
		}
	} else {
		toast.open({
			type: 'danger',
			title: $format('reboot_err_toast', {
				default: 'Error! Reboot command failed.',
			}),
		});
	}
}

export enum ApplianceTypeToColor {
	general = 'rgb(102, 96, 237)',
	relay = 'rgb(92, 125, 123)',
	light = 'rgb(255, 197, 29)',
	outlet = 'rgb(212, 255, 198)',
	heating = 'rgb(255, 74, 80)',
	entertainment = 'rgb(1, 241, 227)',
}

export enum ApplianceTypeToIcon {
	general = 'microchip',
	relay = 'toggle-on',
	light = 'lightbulb',
	outlet = 'outlet',
	heating = 'heat',
	entertainment = 'tv-retro',
}

export function relayUsageTypes(device: Device) {
	const $format = get(format),
		gen2 = is_gen_2(device);
	// let types = ['general', 'outlet', 'light', 'relay']; //gen1
	// let types = ['Socket', 'Lights', 'Heating', 'Entertainment']; //gen2
	return [
		{
			index: 0,
			title: $format('appliance_type_general', { default: 'general' }),
			value: 'general',
			usage: 'general',
			icon: 'sliders-v',
			color: 'rgb(102, 96, 237)',
		},
		{
			index: 1,
			title: $format('device_type_relay', {
				default: 'relay',
			}),
			value: 'relay',
			usage: 'relay',
			icon: 'wave-square',
			color: 'rgb(92, 125, 123)',
		},
		{
			index: 2,
			title: $format('appliance_type_light', { default: 'light' }),
			value: 'light' + (gen2 ? 's' : ''),
			usage: 'light',
			icon: 'lightbulb',
			color: 'rgb(255, 197, 29)',
		},
		{
			index: 3,
			title: $format('appliance_type_socket', { default: 'socket' }),
			value: 'socket',
			usage: 'outlet',
			icon: 'plug',
			color: 'rgb(212, 255, 198)',
		},
		{
			index: 4,
			title: $format('appliance_type_heating', { default: 'heating' }),
			value: 'heating',
			usage: 'heating',
			icon: 'heat',
			color: 'rgb(255, 74, 80)',
		},
		{
			index: 5,
			title: $format('appliance_type_entertainment', {
				default: 'entertainment',
			}),
			value: 'entertainment',
			usage: 'entertainment',
			icon: 'tv-retro',
			color: 'rgb(1, 241, 227)',
		},
	];
}

export function getRelayUsage(device: Device) {
	const types = relayUsageTypes(device);
	let type = device?.relay_usage;

	if (!type) {
		const settings = devices_settings.getByRealId(device.id);
		type = is_gen_2(device)
			? settings?.sys?.ui_data?.consumption_types?.[device.channel]
			: settings?.[device.category + 's']?.[device.channel]?.appliance_type;
	}
	return types.find((i) => i.value == (type || '').toLowerCase()) || types[0];
}

export interface ApplianceType {
	value: number;
	title: string;
	usage: string;
	icon: string;
	color: string;
	chartColor?: string;
	power?: number;
	selected?: boolean;
}

export function getApplianceTypes(): ApplianceType[] {
	const $format = get(format);

	return [
		{
			value: APPLIANCE_TYPES_VALUES.unknown,
			title: $format('appliance_type_unknown', { default: 'unknown' }),
			usage: 'unknown',
			icon: 'sliders-v',
			color: 'unknown',
			chartColor: getSystemStyle('--color-unknown'),
		},
		{
			value: APPLIANCE_TYPES_VALUES.light,
			title: $format('appliance_type_lighting', {
				default: 'lighting',
			}),
			usage: 'light',
			icon: 'lightbulb',
			color: 'lightning',
			chartColor: getSystemStyle('--color-lightning'),
		},
		{
			value: APPLIANCE_TYPES_VALUES.entertainment,
			title: $format('appliance_type_entertainment', {
				default: 'entertainment',
			}),
			usage: 'entertainment',
			icon: 'tv-retro',
			color: 'entertainment',
			chartColor: getSystemStyle('--color-entertainment'),
		},
		{
			value: APPLIANCE_TYPES_VALUES.heating,
			title: $format('appliance_type_heating_cooling', {
				default: 'heating and cooling',
			}),
			usage: 'heating',
			icon: 'heat',
			color: 'heating',
			chartColor: getSystemStyle('--color-heating'),
		},
		{
			value: APPLIANCE_TYPES_VALUES.refrigeration,
			title: $format('appliance_type_refrigeration', {
				default: 'refrigeration',
			}),
			usage: 'refrigeration',
			icon: 'refrigerator',
			color: 'refrigeration',
			chartColor: getSystemStyle('--color-refrigeration'),
		},
		{
			value: 6,
			title: $format('appliance_type_laundry_cleaning', {
				default: 'laundry and cleaning',
			}),
			usage: 'laundry_cleaning',
			icon: 'dryer-heat',
			color: 'laundry-cleaning',
			chartColor: getSystemStyle('--color-laundry-cleaning'),
		},
		{
			value: APPLIANCE_TYPES_VALUES.cooking,
			title: $format('appliance_type_cooking', {
				default: 'cooking',
			}),
			usage: 'cooking',
			icon: 'oven',
			color: 'cooking',
			chartColor: getSystemStyle('--color-cooking'),
		},
		{
			value: APPLIANCE_TYPES_VALUES.pool,
			title: $format('appliance_type_pool_garden', {
				default: 'pool and garden',
			}),
			usage: 'pool',
			icon: 'water-ladder',
			color: 'pool',
			chartColor: getSystemStyle('--color-pool'),
		},
		{
			value: APPLIANCE_TYPES_VALUES.electric_vehicle,
			title: $format('appliance_type_electric_vehicle', {
				default: 'electric vehicle',
			}),
			usage: 'electric_vehicle',
			icon: 'charging-station',
			color: 'electric-vehicle',
			chartColor: getSystemStyle('--color-electric-vehicle'),
		},
		{
			value: APPLIANCE_TYPES_VALUES.ventilation,
			title: $format('appliance_type_ventilation', {
				default: 'ventilation',
			}),
			usage: 'ventilation',
			icon: 'fan',
			color: 'ventilation',
			chartColor: getSystemStyle('--color-ventilation'),
		},
		{
			value: APPLIANCE_TYPES_VALUES.water_heater,
			title: $format('appliance_type_water_heater', {
				default: 'water heater',
			}),
			usage: 'water_heater',
			icon: 'heat',
			color: 'water-heater',
			chartColor: getSystemStyle('--color-water-heater'),
		},
		{
			value: APPLIANCE_TYPES_VALUES.other,
			title: $format('appliance_type_other', { default: 'other' }),
			usage: 'other',
			icon: 'microchip',
			color: 'other',
			chartColor: getSystemStyle('--color-other'),
		},
		{
			value: APPLIANCE_TYPES_VALUES.roller,
			title: $format('appliance_type_roller', {
				default: 'roller',
			}),
			usage: 'roller',
			icon: 'blinds',
			color: 'roller',
			chartColor: getSystemStyle('--color-roller'),
		},
		{
			value: APPLIANCE_TYPES_VALUES.garage_door,
			title: $format('appliance_type_garage_door', {
				default: 'garage door',
			}),
			usage: 'garage_door',
			icon: 'garage-open',
			color: 'battery',
			chartColor: getSystemStyle('--color-battery'),
		},
		{
			value: APPLIANCE_TYPES_VALUES.game_console,
			title: $format('appliance_type_game_console', {
				default: 'game console',
			}),
			usage: 'game_console',
			icon: 'gamepad-modern',
			color: 'entertainment',
			chartColor: getSystemStyle('--color-entertainment'),
		},
		{
			value: APPLIANCE_TYPES_VALUES.computer,
			title: $format('appliance_type_computer', {
				default: 'computer',
			}),
			usage: 'computer',
			icon: 'display',
			color: 'entertainment',
			chartColor: getSystemStyle('--color-entertainment'),
		},
		{
			value: APPLIANCE_TYPES_VALUES.speaker_system,
			title: $format('appliance_type_speaker_system', {
				default: 'speaker system',
			}),
			usage: 'speaker_system',
			icon: 'speaker',
			color: 'entertainment',
			chartColor: getSystemStyle('--color-entertainment'),
		},
		{
			value: APPLIANCE_TYPES_VALUES.projector,
			title: $format('appliance_type_projector', {
				default: 'projector',
			}),
			usage: 'projector',
			icon: 'projector',
			color: 'entertainment',
			chartColor: getSystemStyle('--color-entertainment'),
		},
		// {
		// 	value: 15,
		// 	title: $format('appliance_type_covers', {
		// 		default: 'covers'
		// 	}),
		// 	usage: 'covers',
		// 	icon: 'curtain',
		// 	color: 'rgb(76, 115, 40)'
		// }
	];
}

export function getReturnedEnergyTypes(): ApplianceType[] {
	const $format = get(format);
	return [
		{
			value: 11,
			title: $format('appliance_type_grid_source', {
				default: 'grid source',
			}),
			usage: 'grid_source',
			icon: 'fas fa-bolt',
			color: 'warning',
			chartColor: getSystemStyle(SystemStyles.COLOR_WARNING),
		},
		{
			value: 12,
			title: $format('appliance_type_generated_energy', {
				default: 'generated energy',
			}),
			usage: 'generated_energy',
			icon: 'fas fa-sun',
			color: 'success',
			chartColor: getSystemStyle(SystemStyles.COLOR_SUCCESS),
		},
	];
}

export function getApplianceType(device: Device): ApplianceType {
	const types: ApplianceType[] = getApplianceTypes();
	const sources: ApplianceType[] = getReturnedEnergyTypes();
	const defaultTypeOfOther = types.filter((i) => i.usage === 'other')?.[0];
	return (
		types.filter((i) => i.value == device?.appliance_type)?.[0] ||
		sources.filter((i) => i.value == device?.appliance_type)?.[0] ||
		//types[12] is a hardcoded reference and its value can change, so
		defaultTypeOfOther
	);
}
export function getApplianceTypeByUsage(usage: string): ApplianceType {
	const types: ApplianceType[] = getApplianceTypes();
	const sources: ApplianceType[] = getReturnedEnergyTypes();
	const defaultTypeOfOther = types.filter((i) => i.usage === 'other')?.[0];
	return (
		types.filter((i) => i.usage == usage)?.[0] ||
		sources.filter((i) => i.usage == usage)?.[0] ||
		//types[12] is a hardcoded reference and its value can change, so
		defaultTypeOfOther
	);
}

export function sleepingResponse(res: SettingRequest | JrpcResponseData, deviceType: string): string {
	if (SLEEPING_TYPES.includes(deviceType) && (res?.errors || res?.error || res?.data?.sleeping)) {
		const $format = get(format);
		let alertValues;
		if (deviceType === 'S4SN-1031Z' || deviceType === 'S4SN-0031Z' || deviceType === 'SMSN-0031ZL') {
			//heiman smoke sensors
			alertValues = $format('gen2_plus_sleeping_info_toast', {
				default: `Your Shelly device is currently sleeping. Press and hold the device reset button for 5 seconds to wake up your device. `,
			});
		} else {
			const n = deviceType === 'SNSN-0031Z' || deviceType === 'S3SN-0082A' ? 3 : 1;
			alertValues = $format('gen2_sleeping_info_toast', {
				values: { n },
				default: `Your Shelly device is currently sleeping. Please press device button ${n} 
			times to wake up your device. `,
			});
		}
		return alertValues;
	}
	return null;
}

export function castUsageStringToRelayUsageParam(selected) {
	if (selected.value == 2 && selected.usage == 'lighting') {
		return 'light';
	}
	return selected.usage;
}

export function getDevicesCurrentApplianceTypes() {
	const allApplianceTypes = [...getApplianceTypes(), ...getReturnedEnergyTypes()]
		.filter((at) => {
			if (at && at.value !== 0) {
				return at;
			}
		})
		.map((type) => {
			type.title = capFirstChar(type.title);
			return type;
		});

	//here we want to add appliance_type prop to all devices that have PM, but don't have the appliance_type prop
	const devicesWithApplianceType = Object.values(get(devices))
		.map((device) => {
			if (typeof device.appliance_type !== 'undefined') {
				return device;
			}

			const config = devices_config[device.type];
			if (config?.isPM) {
				switch (device.category) {
					case 'light':
						return {
							...device,
							appliance_type: APPLIANCE_TYPES_VALUES.light,
						}; // 2 => lighting
					case 'roller':
						return {
							...device,
							appliance_type: APPLIANCE_TYPES_VALUES.roller,
						}; // 15 => roller
					default:
						return {
							...device,
							appliance_type: APPLIANCE_TYPES_VALUES.other,
						}; // 14 => other
				}
			}

			return device;
		})
		.filter((device) => device?.appliance_type);

	//all devices with appliance types in the current account

	const applianceTypes = allApplianceTypes
		.filter((type) =>
			devicesWithApplianceType.some(
				(appl) => type.value === appl.appliance_type || type.usage === appl.category,
			),
		)
		.map((el) => ({
			value: el.value,
			title: el.title,
			icon: el.icon,
			selected: false,
		}));

	return applianceTypes;
}

export const deviceSettingFormatter = (
	device,
	settings,
	deviceSettings,
	deviceStatuses,
	tab,
	isSidepanel,
) => {
	deviceSettings = deviceSettings ?? {};
	let settings_holder = [];
	const isTestUser = isDevServerUser(['repo', 'nb']) || isT1User();
	const isOffline = get(getDeviceState(device?.id))?.type === 0;
	const isSleepingDevice = SLEEPING_TYPES.includes(device.type);
	const isBluetooth = tab === 'bluetooth';

	if (!device.shared && device.category !== 'virtual_device' && tab === 'application_settings') {
		const mode = get_mode(device, deviceStatuses, deviceSettings);
		(devices_config[device.type]?.modes[mode]?.notifications || [])
			.filter((notification) => /offline/.test(notification))
			.sort((a, b) => {
				return a.localeCompare(b);
			})
			.forEach((notification) => {
				if (!settings.find((setting) => setting.title === notification + '_notifications')) {
					if (
						(devices_config[device.type]?.isPM &&
							/^(power_meter|relay|emeter)$/.test(device.category)) ||
						Object.values(settings).findIndex((x) => x.title === 'ai_model') > -1 //to rend notifications after ai model
					) {
						settings.splice(1, 0, {
							title: `${notification}_notifications`,
							component: notification + '_notifications_settings',
						});
					} else {
						settings.unshift({
							title: `${notification}_notifications`,
							component: notification + '_notifications_settings',
						});
					}
				}
			});
	}

	const hasTempFavPositionsSetting = settings.find(
		(setting) => setting.title === 'temperature_favorite_positions',
	);
	const isThermostatEnabled = typeof readDeviceSettings(device.id)?.['thermostat:0'] !== 'undefined';

	if (tab === 'sensor_settings') {
		if (
			!hasTempFavPositionsSetting &&
			(isTRV(device?.type) ||
				isBluTRV(device?.type) ||
				(isWallDisplay(device?.type) && isThermostatEnabled))
		) {
			settings.splice(0, 0, {
				title: 'temperature_favorite_positions',
				component: 'temperature_favorite_positions',
			});
		}

		if (hasTempFavPositionsSetting && isWallDisplay(device?.type) && !isThermostatEnabled) {
			settings.splice(0, 1);
		}
	}

	const checkDeviceNoteAndUsageDetails =
		!settings.find((setting) => setting.title === 'device_note') &&
		!settings.find((setting) => setting.title === 'device_usage_details');

	if (
		!device?.shared &&
		tab === 'networks' &&
		deviceSettings.hasOwnProperty('matter') &&
		!settings.find((setting) => setting.title === 'matter')
	) {
		settings.splice(settings.length - 1, 0, {
			title: 'matter',
			component: 'matter_gen_2',
		});
	}

	// FE-1997: expose Gen2+ device password protection (Shelly.SetAuth) in the device Settings tab.
	// Injected centrally so every Gen2+ device gets it without editing each device's settings config.
	if (
		!device?.shared &&
		tab === 'settings' &&
		is_gen_2(device) &&
		!settings.find((setting) => setting.title === 'authentication')
	) {
		const factoryResetIndex = settings.findIndex((setting) => setting.title === 'factory_reset');
		const deviceInfoIndex = settings.findIndex((setting) => setting.title === 'device_information');
		const insertIndex =
			factoryResetIndex !== -1
				? factoryResetIndex
				: deviceInfoIndex !== -1
					? deviceInfoIndex
					: settings.length;
		settings.splice(insertIndex, 0, {
			title: 'authentication',
			component: 'device_authentication_gen_2',
		});
	}

	// FE-2508: new layout promoted Factory Reset to an action button, which hid
	// the "from input" toggles. Re-expose them as a Device Settings drill-down.
	if (
		tab === 'settings' &&
		isSidepanel &&
		shouldShowFactoryResetFromInputSetting(device) &&
		!settings.find((setting) => setting.title === 'factory_reset_from_input')
	) {
		const factoryResetFromInputIndex = settings.findIndex(
			(setting) => setting.title === 'factory_reset',
		);
		const deviceInfoForResetIndex = settings.findIndex(
			(setting) => setting.title === 'device_information',
		);
		const factoryResetFromInputInsertIndex =
			factoryResetFromInputIndex !== -1
				? factoryResetFromInputIndex
				: deviceInfoForResetIndex !== -1
					? deviceInfoForResetIndex
					: settings.length;
		settings.splice(factoryResetFromInputInsertIndex, 0, {
			title: 'factory_reset_from_input',
			component: 'factory_reset_from_input',
		});
	}

	if (tab === 'application_settings') {
		if (device && !device.shared) {
			if (checkDeviceNoteAndUsageDetails) {
				settings.splice(
					settings.length,
					0,
					{
						title: 'device_note',
						component: 'note_section',
					},
					{
						title: 'device_usage_details',
						component: 'information_board',
					},
				);
			}
		}
	}

	if (
		tab === 'settings' &&
		isCloneDeviceSettingsEnabled() &&
		!settings.find((setting) => setting.title === 'apply_settings_configuration')
	) {
		const insertIndex = settings.findIndex((s) => s.title === 'device_information');

		settings.splice(insertIndex, 0, {
			title: 'apply_settings_configuration',
			component: 'apply_settings_configuration',
		});
	}

	if (tab === 'settings' && isSidepanel) {
		const deviceInfoIndex = settings.findIndex((s) => s.title === 'device_information');
		if (deviceInfoIndex !== -1) {
			if (!settings.find((s) => s.title === 'delete_device')) {
				settings.splice(deviceInfoIndex + 1, 0, {
					title: 'delete_device',
					component: 'delete_device',
				});
			}
			const canTransfer =
				!device.shared &&
				!device.shared_by_me &&
				!device.id.includes('_') &&
				!isWallDisplay(device.type) &&
				!is_loqed(device) &&
				!get(user_data)?.installer;
			if (canTransfer && !settings.find((s) => s.title === 'transfer_device')) {
				settings.splice(deviceInfoIndex + 1, 0, {
					title: 'transfer_device',
					component: 'transfer_device',
				});
			}
		}
	}

	// When not in Side Panel (e.g. DeviceDrawer), exclude transfer/delete submenus - they may
	// persist in the shared config from a previous Side Panel session after layout toggle
	if (tab === 'settings' && !isSidepanel) {
		settings = settings.filter(
			(s) =>
				s.title !== 'transfer_device' &&
				s.title !== 'delete_device' &&
				s.title !== 'factory_reset_from_input',
		);
	}

	if (isBluetooth && isSleepingDevice) {
		settings = settings.filter((setting) => setting.title !== 'assigned_bluetooth_devices');
	}

	settings.forEach((element, index) => {
		if (typeof element.test_user !== 'undefined' && !isTestUser) {
			return;
		}

		if (element.title === 'cloud' && isOffline) {
			return;
		}

		// Filter channel specific settings
		if (typeof element.supported_mode !== 'undefined') {
			const supportedModes = Array.isArray(element.supported_mode)
				? element.supported_mode
				: [element.supported_mode];
			if (!supportedModes.includes(device?.mode)) return;
		}

		if (isWallDisplay(device.type)) {
			// Channel devices have IDs like deviceId_1, deviceId_2 (not _100 or _101)
			const isChannelDevice = isSecondaryWallDisplay(device);

			// Hide thermostat and media player settings for wall display channel devices
			if (
				isChannelDevice &&
				(element.title === 'thermostat_settings' || element.title === 'media_player_settings')
			) {
				return;
			}
		}

		if (
			element.component === 'local_network_messaging_g2' &&
			isLocalNetworkMessagingExcludedDeviceType(device?.type)
		) {
			return;
		}

		/**
		 * The Bluetooth RPC tester drives the native BLE bridge and only
		 * makes sense on Gen 2+ devices (which expose the JRPC frame
		 * protocol over BLE). Hide it entirely on Gen 1 hardware, where
		 * the bridge has no contract to talk to.
		 */
		if (element.component === 'bluetooth_rpc_tester' && !is_gen_2(device)) {
			return;
		}

		// Only add setting if component exists in settings_list
		const componentKey =
			isSidepanel && COMPONENTS_WITH_LAYOUT_V2.has(element.component)
				? element.component + '_v2'
				: element.component;
		const component = settings_list[componentKey];
		if (!component) {
			return;
		}

		if (
			element.requires_operational_counters &&
			!hasOperationalCountersInStatus(device, deviceStatuses)
		) {
			return;
		}

		if (
			element.requires_config_key &&
			!hasComponentConfigKeyInSettings(device, deviceSettings, element.requires_config_key)
		) {
			return;
		}

		settings_holder.push({
			component,
			componentKey: element.component,
			title:
				element.title === 'invert_switch'
					? 'invert_switch_label'
					: 'sub_settings_title_' + element.title,
			titleDefault: element.title,
			type: element.values !== undefined ? 'i18n_num_replace' : '',
			values: element.values,
			ex: true,
			open: false,
			props: buildSettingProps(element, device, isSidepanel),
			beta: element?.beta,
			soon: element?.soon,
			unsupported_fw: verifySettingUnsupportedFWVersion(
				device,
				deviceSettings,
				element?.min_ver,
				deviceStatuses,
			),
			test_setting: element.test_user,
			premium: Boolean(element.premium) || PREMIUM_SETTING_TITLES.has(element.title),
		});

		if (!is_gen_2(device) && element?.min_ver && element.beta) {
			settings_holder.splice(index, 1);
		}
		if (
			readDeviceStatus(get_gen_2_component_channel_structure(device))?.flags?.includes(
				'uncalibrated',
			)
		) {
			if (settings_holder[index].component === '"max_current_protection_g2"') {
				settings_holder.splice(index, 1);
			}
		}
	});

	if (tab === 'application_settings' && device?.shared) {
		settings_holder = settings_holder.filter((x) =>
			/(pin_lock|device_card_customization)$/.test(x.title),
		);
	}

	if (!isFanDevice(device?.type) && deviceSettings?.ext_temperature?.length < 1) {
		settings_holder.forEach((x, i) => {
			if (x.title.includes('temperature')) {
				settings_holder.splice(i, 1);
			}
		});
	}

	if (!isFanDevice(device?.type) && deviceStatuses?.ext_humidity?.length < 1) {
		settings_holder.forEach((x, i) => {
			if (x.title.includes('hum')) {
				settings_holder.splice(i, 1);
			}
		});
	}

	// remove all settings for solus
	if (isVendorSpecificFW(deviceSettings?.[device.id], 'Solus')) {
		settings_holder = settings_holder.filter(
			(x) =>
				!x.title.includes('firmware') &&
				!x.title.includes('switch') &&
				!x.title.includes('long') &&
				!x.title.includes('led') &&
				!x.title.includes('consumption') &&
				!x.title.includes('eco') &&
				!x.title.includes('supply') &&
				!x.title.includes('appliance') &&
				!x.title.includes('roaming'),
		);
	}

	if (
		tab === 'settings' &&
		!isSidepanel &&
		// device.gen !== 'GBLE' &&//
		(device.shared || get(getDeviceState(device.id))?.type < defaultStateType)
	) {
		settings_holder = settings_holder.filter(
			(x) =>
				x.title.includes('device_information') ||
				(x.title.includes('firmware') &&
					!device.shared &&
					get(getDeviceState(device?.id))?.type === 7),
		);
	}

	return settings_holder;
};

/**
 * Helper for assigning label to collapse title for the old layout and sub settings for the new layout
 */
export const handleCollapseTitleLabel = (setting) => {
	if (/lowBattery_notifications/.test(setting?.titleDefault || setting?.title)) {
		return {
			icon: 'fa-sharp-duotone fa-solid fa-gem',
			value: get(format)('label_new', { default: 'NEW' }),
			backgroundColor: '--color-premium' as SystemColorsTypes,
			type: 'premium' as const,
		};
	} else if (
		/temperature_favorite_positions|quick_tour_guide|introduction_wizard|remote_controls|operational_counters|bluetooth_rpc_tester/.test(
			setting?.titleDefault || setting?.title,
		)
	) {
		return {
			value: get(format)('label_new', { default: 'NEW' }),
			backgroundColor: '--color-secondary' as SystemColorsTypes,
			type: 'secondary' as const,
		};
	}
	return {
		value: '',
		backgroundColor: '' as SystemColorsTypes,
		type: 'default' as const,
	};
};
